/**
 * Face of Belvoir Service
 * Handles all REST API calls for the "Be the Face of Belvoir" competition.
 *
 * Endpoints (WordPress mu-plugin: face-of-belvoir.php):
 *   POST   /custom/v1/face-of-belvoir                    — submit entry (public)
 *   GET    /custom/v1/face-of-belvoir                    — list all entries (admin)
 *   POST   /custom/v1/face-of-belvoir/{id}/status        — update status (admin)
 *   DELETE /custom/v1/face-of-belvoir/{id}               — delete entry (admin)
 *   GET    /custom/v1/face-of-belvoir/{id}/photo         — get photo (admin)
 */

import config from '../config/environment';

const API_BASE = `${config.apiEndpoint}/custom/v1`;

const headers = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

async function handleResponse(response) {
  const text = await response.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Server returned non-JSON response (${response.status}): ${text.slice(0, 200)}`);
  }
  if (!response.ok) {
    const message =
      json?.message || json?.data?.message || `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(message);
  }
  return json;
}

/**
 * Read a File object as a base64 data URI.
 * @param {File} file
 * @returns {Promise<string>}
 */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Submit a competition entry.
 *
 * @param {Object} entry
 * @param {string} entry.name
 * @param {string} entry.email
 * @param {string} entry.phone
 * @param {string} entry.address
 * @param {string} entry.familyInfo
 * @param {string} entry.availability
 * @param {string} entry.photoData        — base64 data URI
 * @param {string} entry.photoFileName    — original filename
 * @param {boolean} entry.marketingConsent
 * @returns {Promise<{ id: number, message: string }>}
 */
export async function submitEntry(entry) {
  const response = await fetch(`${API_BASE}/face-of-belvoir`, {
    method: 'POST',
    headers,
    body: JSON.stringify(entry),
  });
  const json = await handleResponse(response);
  return json.data;
}

/**
 * Fetch all competition entries (admin).
 * @returns {Promise<Array>}
 */
export async function getAllEntries() {
  const response = await fetch(`${API_BASE}/face-of-belvoir`, {
    method: 'GET',
    headers,
  });
  const json = await handleResponse(response);
  return json.data ?? [];
}

/**
 * Update the status of an entry (admin).
 * @param {number} id
 * @param {string} status — 'new' | 'reviewing' | 'shortlisted' | 'rejected' | 'winner'
 * @param {string} [notes]
 */
export async function updateEntryStatus(id, status, notes = '') {
  const response = await fetch(`${API_BASE}/face-of-belvoir/${id}/status`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ status, notes }),
  });
  return handleResponse(response);
}

/**
 * Delete an entry (admin).
 * @param {number} id
 */
export async function deleteEntry(id) {
  const response = await fetch(`${API_BASE}/face-of-belvoir/${id}`, {
    method: 'DELETE',
    headers,
  });
  return handleResponse(response);
}

/**
 * Fetch a photo as base64 (admin).
 * @param {number} id
 * @returns {Promise<{ fileName: string, mimeType: string, content: string }>}
 */
export async function getEntryPhoto(id) {
  const response = await fetch(`${API_BASE}/face-of-belvoir/${id}/photo`, {
    method: 'GET',
    headers,
  });
  const json = await handleResponse(response);
  return json.data;
}

/**
 * Open the family photo in a new browser tab.
 * @param {number} id
 */
export async function openEntryPhoto(id) {
  const photo = await getEntryPhoto(id);
  const byteChars = atob(photo.content);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: photo.mimeType });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
